import { useState, useRef, useEffect } from "react";
import { X, RefreshCw } from "lucide-react";
import { ReviewEditModal } from "./ReviewEditModal";

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberEmail: string;
  onSaveReport?: (data: any) => void;
  initialObservation?: string;
}

// Default topic choices
const DEFAULT_TOPICS = [
  "AI Bias or Risk",
  "System Performance",
  "Data Quality",
  "User Experience",
  "Security Concern",
  "Event",
  "Other"
];

// Simplified sentiment options matching the image
const sentimentOptions = [
  { emoji: "😠", label: "Angry" },
  { emoji: "😕", label: "Confused" },
  { emoji: "🧐", label: "Curious" },
  { emoji: "😊", label: "Happy" },
  { emoji: "😢", label: "Sad" },
  { emoji: "😤", label: "Frustrated" },
  { emoji: "😐", label: "Meh" },
  { emoji: "😌", label: "Satisfied" },
];

export function ReportIssueModal({ isOpen, onClose, subscriberEmail, onSaveReport, initialObservation }: ReportIssueModalProps) {
  const [topic, setTopic] = useState("AI Bias or Risk");
  const [customTopic, setCustomTopic] = useState("");
  const [showCustomTopicInput, setShowCustomTopicInput] = useState(false);
  const [location, setLocation] = useState("");
  const [observation, setObservation] = useState(
    initialObservation || 
    "I saw several participants struggling to navigate the information system during the learning event, delaying access to critical information and causing errors. This directly risks data quality, integrity, and availability."
  );
  const [actionType, setActionType] = useState<"IMPROVE" | "SUSTAIN">("IMPROVE");
  const [recommendation, setRecommendation] = useState("");
  const [selectedSentiment, setSelectedSentiment] = useState<string | null>(null);
  const [isReviewEditOpen, setIsReviewEditOpen] = useState(false);
  const [isMobileCompact, setIsMobileCompact] = useState(false);
  const observationRef = useRef<HTMLTextAreaElement>(null);

  // Update observation when initialObservation prop changes
  useEffect(() => {
    if (initialObservation) {
      setObservation(initialObservation);
    }
  }, [initialObservation]);

  // Handle mobile viewport responsiveness
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 640;
      setIsMobileCompact(isMobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle topic selection change
  const handleTopicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "custom") {
      setShowCustomTopicInput(true);
      setTopic("");
    } else {
      setShowCustomTopicInput(false);
      setCustomTopic("");
      setTopic(value);
    }
  };

  const handleReset = () => {
    setTopic("AI Bias or Risk");
    setCustomTopic("");
    setShowCustomTopicInput(false);
    setObservation("I saw several participants struggling to navigate the information system during the learning event, delaying access to critical information and causing errors. This directly risks data quality, integrity, and availability.");
    setActionType("IMPROVE");
    setRecommendation("");
    setSelectedSentiment(null);
  };

  if (!isOpen && !isReviewEditOpen) return null;

  const handleSubmit = () => {
    // Open Review & Edit modal instead of saving directly
    setIsReviewEditOpen(true);
  };

  const handleReviewEditClose = () => {
    setIsReviewEditOpen(false);
    // Reset form
    handleReset();
    // Close the report issue modal
    onClose();
  };

  const finalTopic = customTopic || topic;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      {/* Modal Container - Compact layout for 853px max height */}
      <div className="relative w-full max-w-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative w-full bg-white flex flex-col max-h-[853px] overflow-y-auto">
          {/* Header - Reduced padding, moved 8px lower */}
          <div className={`sticky top-0 bg-white border-b border-gray-200 flex items-center justify-between mt-[8px] ${
            isMobileCompact ? 'px-4 py-2' : 'px-5 py-3'
          }`}>
            <h2 className="text-lg font-bold text-black">
              Quick Observation
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Reset form"
              >
                <RefreshCw className="size-4 text-gray-600" />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="size-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Form Content - Compact spacing for mobile */}
          <div className={`flex-1 ${
            isMobileCompact ? 'px-4 py-1.5 space-y-1.5' : 'px-5 py-2 space-y-2'
          }`}>

            {/* Topic */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">
                Topic:
              </label>
              {!showCustomTopicInput ? (
                <div className="relative">
                  <select
                    value={topic}
                    onChange={handleTopicChange}
                    className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none pr-9"
                  >
                    {DEFAULT_TOPICS.map((topicOption) => (
                      <option key={topicOption} value={topicOption}>
                        {topicOption}
                      </option>
                    ))}
                    <option value="custom">Enter your own...</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="size-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <input
                    type="text"
                    value={customTopic}
                    onChange={(e) => setCustomTopic(e.target.value)}
                    placeholder="Enter custom topic..."
                    className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setShowCustomTopicInput(false);
                      setCustomTopic("");
                      setTopic(DEFAULT_TOPICS[0]);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    ← Back to default topics
                  </button>
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">
                Location:
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location..."
                className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Observation or Issue */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">
                Observation or Issue:
              </label>
              <textarea
                ref={observationRef}
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="I saw several participants struggling to navigate the information system during the learning event, delaying access to critical information and causing errors. This directly risks data quality, integrity, and availability."
                className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none min-h-[100px]"
              />
            </div>

            {/* Action Type */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block flex items-center gap-1">
                Action Type: <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setActionType("IMPROVE")}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                    actionType === "IMPROVE"
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  IMPROVE
                </button>
                <button
                  onClick={() => setActionType("SUSTAIN")}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                    actionType === "SUSTAIN"
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  SUSTAIN
                </button>
              </div>
            </div>

            {/* Recommendation (Optional) */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">
                Recommendation (Optional):
              </label>
              <textarea
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
                placeholder="Add any additional details or context here..."
                className="w-full px-3 py-2 text-sm text-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:text-black resize-none min-h-[80px]"
              />
            </div>

            {/* Sentiment */}
            <div>
              <label className="text-xs font-semibold text-black mb-2 block flex items-center gap-1">
                How are you feeling about this? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {sentimentOptions.map((sentiment) => (
                  <button
                    key={sentiment.label}
                    onClick={() => setSelectedSentiment(sentiment.label)}
                    className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl border-2 transition-all ${
                      selectedSentiment === sentiment.label
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                    title={sentiment.label}
                  >
                    <span className="text-2xl mb-0.5">{sentiment.emoji}</span>
                    <span className="text-[10px] text-black font-medium text-center leading-tight">
                      {sentiment.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!observation || !selectedSentiment || !finalTopic}
              className={`w-full py-3 text-white rounded-xl font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 ${
                actionType === "IMPROVE" || actionType === "SUSTAIN"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              Send
            </button>
          </div>
        </div>
      </div>

      {/* Review & Edit Modal */}
      <ReviewEditModal
        isOpen={isReviewEditOpen}
        onClose={handleReviewEditClose}
        observationData={{
          topic: finalTopic,
          observation,
          type: actionType.toLowerCase() as "event" | "routine",
          pointOfContact: "",
          location: location,
          sentiment: selectedSentiment,
          actionType,
          recommendation
        }}
        subscriberEmail={subscriberEmail}
        onSaveReport={onSaveReport}
      />
    </div>
  );
}